import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/shared/material.module';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { GroupsService } from '../groups.service';

export interface GroupFormData {
  group?: any; // Pass the whole group object for editing
  action: 'create' | 'edit';
}

@Component({
  selector: 'app-group-form',
  templateUrl: './group-form.component.html',
  styleUrls: ['./group-form.component.scss'],
  standalone: true,
  imports: [MaterialModule, TranslateModule, FormsModule],
})
export class GroupFormComponent implements OnInit {
  groupName = '';
  groupDescription = '';
  loading = false;
  isEditMode = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GroupFormData,
    private dialogRef: MatDialogRef<GroupFormComponent>,
    private groupsService: GroupsService,
    private toast: ToastService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.isEditMode = this.data.action === 'edit' && !!this.data.group;
    if (this.isEditMode) {
      this.groupName = this.data.group.actualName || '';
      this.groupDescription = this.data.group.actualDescription || '';
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.loading = true;
    if (this.isEditMode) {
      this.updateGroup();
    } else {
      this.createGroup();
    }
  }

  private createGroup(): void {
    this.groupsService
      .createGroup({
        name: this.groupName,
        description: this.groupDescription,
        language: this.translate.currentLang,
      })
      .subscribe({
        next: () => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
          this.dialogRef.close(true); // Pass true to indicate success
        },
        error: (err) => {
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.CreatingGroupFail'), 'Ok', { duration: 2000 });
        },
      });
  }

  private updateGroup(): void {
    const lang = this.translate.currentLang;
    // We need to update both name and description
    const updateName$ = this.groupsService.editGroupName({ _id: this.data.group._id, name: this.groupName });
    const updateDesc$ = this.groupsService.editGroupDescription({
      _id: this.data.group._id,
      description: this.groupDescription,
      language: lang,
    });

    // Since the backend has separate endpoints, we'll have to call them sequentially.
    // A better backend would have a single endpoint for updating a group.
    updateName$.subscribe({
      next: () => {
        updateDesc$.subscribe({
          next: () => {
            this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.loading = false;
            this.toast.snackbar.open(this.translate.instant('Snackbar.EditingGroupFail'), 'Ok', { duration: 2000 });
          },
        });
      },
      error: (err) => {
        this.loading = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.EditingGroupFail'), 'Ok', { duration: 2000 });
      },
    });
  }
}
